// LLM generated - Model Claude 4 Sonnet
import type { ChatMessage as DesignSystemChatMessage } from '~/domains/design-system/components/Chat/ChatMessagesList/types';
import type { PostProps } from '~/domains/design-system/components/Chat/Post/types';
import type { ChatMessage as LiveActivityChatMessage } from '~/services/live-activity/types';

// Type for optimistic reaction merge functions
type ReactionMergeFunction = (
  messageId: string,
  serverReactions: { code: string; count: number }[],
  serverUserReactions?: string[]
) => { code: string; count: number }[];

type UserReactionMergeFunction = (messageId: string, serverUserReactions: string[]) => string[];

type OptimisticReactionOptions = {
  getReactionsWithOptimistic?: ReactionMergeFunction;
  getUserReactionsWithOptimistic?: UserReactionMergeFunction;
};

// Helper function to convert API messages to design system format
export const adaptApiMessageToDesignSystem = (
  apiMessage: LiveActivityChatMessage,
  currentUserHandle?: string,
  optimisticOptions?: OptimisticReactionOptions
): DesignSystemChatMessage => {
  const isCurrentUser = apiMessage.user.handle === currentUserHandle;
  const variant: 'other' | 'current' = isCurrentUser ? 'current' : 'other';

  // Server's userReactions for this message (used to detect if server caught up)
  const serverUserReactions = apiMessage.userReactions || [];

  // Apply optimistic merge if functions provided, otherwise use server data
  // Pass serverUserReactions to getReactionsWithOptimistic so it can detect
  // if the server has caught up and avoid double-counting
  const mergedReactions = optimisticOptions?.getReactionsWithOptimistic
    ? optimisticOptions.getReactionsWithOptimistic(
        apiMessage.id,
        apiMessage.reactions,
        serverUserReactions
      )
    : apiMessage.reactions;

  const mergedUserReactions = optimisticOptions?.getUserReactionsWithOptimistic
    ? optimisticOptions.getUserReactionsWithOptimistic(apiMessage.id, serverUserReactions)
    : serverUserReactions;

  return {
    id: apiMessage.id,
    avatar: apiMessage.user.avatarUrl || '',
    handle: apiMessage.user.handle,
    message: apiMessage.payload.message,
    timestamp: new Date(apiMessage.publishedAt).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    }),
    reactions: mergedReactions
      .sort((a, b) => b.count - a.count) // Sort by count (highest first)
      .slice(0, 5) // Take only top 5 reactions
      .map((r) => r.code),
    reactionCount: mergedReactions.reduce((sum, r) => sum + r.count, 0),
    userReactions: mergedUserReactions,
    isCommissioner: apiMessage.splashMessage,
    isCurrentUser,
    variant,
  };
};

// Helper function to convert API messages to Post component format
export const adaptApiMessageToPost = (
  apiMessage: LiveActivityChatMessage,
  timestamp: string,
  currentUserHandle?: string,
  onEditPost?: (messageId: string, chatId: string, title: string, content: string) => void,
  onDeletePost?: (messageId: string, chatId: string) => void,
  onPinPost?: (messageId: string, isPinned: boolean) => void,
  onMutePost?: (posterHandle: string) => void,
  onReportPost?: (handle: string, messageId: string) => void,
  chatId?: string,
  isCurrentUserCommissioner?: boolean,
  isMuted?: boolean,
  optimisticOptions?: OptimisticReactionOptions
): PostProps => {
  const isCurrentUser = apiMessage.user.handle === currentUserHandle;

  // Server's userReactions for this post (used to detect if server caught up)
  const serverUserReactions = apiMessage.userReactions || [];

  // Apply optimistic merge if functions provided, otherwise use server data
  // Pass serverUserReactions to getReactionsWithOptimistic so it can detect
  // if the server has caught up and avoid double-counting
  const mergedReactions = optimisticOptions?.getReactionsWithOptimistic
    ? optimisticOptions.getReactionsWithOptimistic(
        apiMessage.id,
        apiMessage.reactions,
        serverUserReactions
      )
    : apiMessage.reactions;

  const mergedUserReactions = optimisticOptions?.getUserReactionsWithOptimistic
    ? optimisticOptions.getUserReactionsWithOptimistic(apiMessage.id, serverUserReactions)
    : serverUserReactions;

  return {
    id: apiMessage.id,
    avatar: apiMessage.user.avatarUrl || '',
    handle: apiMessage.user.handle,
    title: apiMessage.payload.header || 'Post',
    content: apiMessage.payload.message,
    timestamp,
    isPinned: apiMessage.isPinned || false,
    isCommissioner: isCurrentUserCommissioner || false,
    reactionCount: mergedReactions.reduce((sum, r) => sum + r.count, 0),
    reactions: mergedReactions
      .sort((a, b) => b.count - a.count) // Sort by count (highest first)
      .slice(0, 5) // Take only top 5 reactions
      .map((r) => r.code),
    userReactions: mergedUserReactions,
    isExpandable: apiMessage.payload.message.length > 200,
    isExpanded: false,
    isCurrentUser,
    onToggleExpand: () => {},
    onEditClick: () => {
      if (onEditPost && chatId) {
        onEditPost(
          apiMessage.id,
          chatId,
          apiMessage.payload.header || 'Post',
          apiMessage.payload.message
        );
      }
    },
    onPinClick: () => {
      if (onPinPost) {
        onPinPost(apiMessage.id, apiMessage.isPinned || false);
      }
    },
    onDeleteClick: () => {
      if (onDeletePost && chatId) {
        onDeletePost(apiMessage.id, chatId);
      }
    },
    onMuteClick: () => {
      if (onMutePost) {
        onMutePost(apiMessage.user.handle);
      }
    },
    onReportClick: () => {
      if (onReportPost) {
        onReportPost(apiMessage.user.handle, apiMessage.id);
      }
    },
    isMuted: isMuted ?? false,
  };
};

// Helper function to get the most recent message across all chats
export const getMostRecentMessage = (
  messagesChatHistory: LiveActivityChatMessage[],
  postsChatHistory: LiveActivityChatMessage[],
  timestamp: string
): DesignSystemChatMessage | undefined => {
  const allMessages = [...messagesChatHistory, ...postsChatHistory];

  if (allMessages.length === 0) {
    return undefined;
  }

  const sortedMessages = allMessages.sort(
    (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
  );

  const mostRecentMessage = sortedMessages[0];

  return {
    id: mostRecentMessage.id,
    avatar: mostRecentMessage.user.avatarUrl || '',
    handle: mostRecentMessage.user.handle,
    message: mostRecentMessage.payload.message,
    timestamp,
  };
};
